import { t } from 'i18next';
import stringify from 'json-stable-stringify';
import Swal, { SweetAlertResult } from 'sweetalert2';

import * as apiPostSearch from 'apiAuto/backend/paths/PostAccessorySearchV1';
import { AccessorySortOrder as AccessorySortOrderApi } from 'apiAuto/backend/schemas/AccessorySortOrder';
import { Characteristic } from 'apiAuto/backend/schemas/Characteristic';
import { UrlParams } from 'enums/Accessory/Search/UrlParams';
import { AccessorySortOrder } from 'enums/SortOrder/AccessorySortOrder';
import delay from 'functions/delay';
import { AirUrlParams, AirUrlParamsClass } from 'hooks/useAirNavigation';
import * as Accessory from 'types/Accessory/Search/Accessory';
import * as SliderWithIdList from 'types/Accessory/Search/SliderWithIdList';
import * as StringList from 'types/_general/Search/StringList';

const defaultPageSize = 20;
const defaultPageIndex = 0;
const defaultSortOrder = AccessorySortOrder.NameAsc;

export type Type = {
	categoryIds: StringList.Type;
	characteristics: StringList.Type;
	co2LabelIds: StringList.Type;
	currencyCode: string | null;
	pageIndex: number;
	pageSize: number;
	preferredNutrientIds: StringList.Type;
	searchTerm: string;
	showOnlyDeleted: boolean;
	sliders: SliderWithIdList.Type;
	sortOrder: AccessorySortOrder;
	tagIds: StringList.Type;
};

export function create(): Type {
	return {
		categoryIds: StringList.create(),
		characteristics: StringList.create(),
		co2LabelIds: StringList.create(),
		currencyCode: null,
		pageIndex: defaultPageIndex,
		pageSize: defaultPageSize,
		preferredNutrientIds: [],
		searchTerm: '',
		showOnlyDeleted: false,
		sliders: SliderWithIdList.create(),
		sortOrder: defaultSortOrder,
		tagIds: StringList.create(),
	};
}

export function hasAdvancedSearchParameters(obj: Type): boolean {
	return (
		stringify({
			...obj,
			searchTerm: undefined,
			currencyCode: null,
			showOnlyDeleted: undefined,
			preferredNutrientIds: [],
		}) !==
		stringify({
			...create(),
			searchTerm: undefined,
			currencyCode: null,
			showOnlyDeleted: undefined,
			preferredNutrientIds: [],
		})
	);
}

export function setShowAdvancedSearch(obj: Type, input: boolean) {
	return { ...obj, showAdvancedSearch: input };
}

export function reset(): Type {
	return create();
}

export function changeCategories(obj: Type, items: string[]): Type {
	return { ...obj, categoryIds: items };
}

export function changeTags(obj: Type, items: string[]): Type {
	return { ...obj, tagIds: items };
}

export function changeCharacteristics(obj: Type, input: { saved: string; all: string[] }): Type {
	const resultCharacteristics = [...obj.characteristics];

	if (resultCharacteristics.length === 0)
		return { ...obj, characteristics: input.all.filter((x) => x != input.saved) };
	if (resultCharacteristics.length === 1 && resultCharacteristics.includes(input.saved))
		return { ...obj, characteristics: input.all.filter((x) => x != input.saved) };
	if (resultCharacteristics.includes(input.saved))
		return { ...obj, characteristics: resultCharacteristics.filter((x) => x != input.saved) };
	resultCharacteristics.push(input.saved);
	return { ...obj, characteristics: resultCharacteristics };
}

export function changeSlider(
	obj: Type,
	input: { id: string; left: number | null; right: number | null }
): Type {
	const resultSliders = [...obj.sliders];
	const find = resultSliders.find((x) => x.id === input.id);
	if (find) {
		resultSliders.map((x) => {
			if (x.id === input.id) {
				x.left = input.left;
				x.right = input.right;
			}
		});
	} else {
		resultSliders.push({
			id: input.id,
			left: input.left,
			right: input.right,
		});
	}

	return { ...obj, sliders: resultSliders };
}

export function changeCo2Labels(obj: Type, input: string): Type {
	const resultLabels = obj.co2LabelIds;
	if (resultLabels.includes(input)) {
		return { ...obj, co2LabelIds: resultLabels.filter((x) => x != input) };
	}
	resultLabels.push(input);
	return { ...obj, co2LabelIds: resultLabels };
}

export function changePageIndex(obj: Type, input: number): Type {
	return { ...obj, pageIndex: input };
}

export function changePageSize(obj: Type, input: string): Type {
	return { ...obj, pageSize: Number(input) };
}

export function changeSortOrder(obj: Type, input: string): Type {
	return { ...obj, sortOrder: input as AccessorySortOrder };
}

export function changeSearchTerm(obj: Type, input: string): Type {
	return { ...obj, searchTerm: input };
}

// TODO: find nice solution
export function mapToUrlParamsClass(obj: Type): AirUrlParamsClass {
	const output: AirUrlParams = {};

	if (obj.pageIndex != defaultPageIndex) output[UrlParams.PageIndex] = String(obj.pageIndex);
	if (obj.pageSize != defaultPageSize) output[UrlParams.PageSize] = String(obj.pageSize);
	if (obj.sortOrder != defaultSortOrder) output[UrlParams.SortOrder] = String(obj.sortOrder);
	if (obj.searchTerm != '') output[UrlParams.SearchTerm] = String(obj.searchTerm);
	if (obj.showOnlyDeleted) output[UrlParams.ShowOnlyDeleted] = String(obj.showOnlyDeleted);
	if (obj.characteristics.length > 0)
		output[UrlParams.Characteristics] = StringList.mapToUrl(obj.characteristics);
	if (obj.sliders.length > 0) output[UrlParams.Slider] = SliderWithIdList.mapToUrl(obj.sliders);
	if (obj.co2LabelIds.length > 0)
		output[UrlParams.Co2Label] = StringList.mapToUrl(obj.co2LabelIds);
	if (obj.tagIds.length > 0) output[UrlParams.Tags] = String(StringList.mapToUrl(obj.tagIds));
	if (obj.categoryIds.length > 0)
		output[UrlParams.Categories] = String(StringList.mapToUrl(obj.categoryIds));
	return new AirUrlParamsClass(output);
}

export function getFromUrl(
	p: AirUrlParamsClass,
	preferredNutrientIds: string[],
	currencyCode: string,
	archive: boolean
): Type {
	const params = p.params;

	return {
		categoryIds: StringList.mapFromUrl(params, UrlParams.Categories),
		characteristics: StringList.mapFromUrl(params, UrlParams.Characteristics),
		co2LabelIds: StringList.mapFromUrl(params, UrlParams.Co2Label),
		currencyCode: currencyCode,
		pageIndex: params.pi ? Number(params.pi) : defaultPageIndex,
		pageSize: params.ps ? Number(params.ps) : defaultPageSize,
		preferredNutrientIds: preferredNutrientIds,
		searchTerm: params.st ? params.st : '',
		showOnlyDeleted: archive,
		sliders: SliderWithIdList.mapFromUrl(params, UrlParams.Slider),
		sortOrder: params.so ? (params.so as AccessorySortOrder) : defaultSortOrder,
		tagIds: StringList.mapFromUrl(params, UrlParams.Tags),
	};
}

export function mapToApi(obj: Type): apiPostSearch.RequestBody {
	return {
		...obj,
		...SliderWithIdList.mapToApi(obj.sliders),
		sortOrder: obj.sortOrder as AccessorySortOrderApi,
		carbonDioxideLabelIds: obj.co2LabelIds,
		characteristics: obj.characteristics.map((x) => x as Characteristic),
		tagIds: obj.tagIds,
	};
}

export async function restore(obj: Type, id: string): Promise<Type> {
	const result: SweetAlertResult = await Swal.fire({
		cancelButtonText: t('_general:CANCEL'),
		confirmButtonColor: '#00cc07',
		confirmButtonText: t('_general:RESTORE'),
		icon: 'warning',
		iconColor: '#00cc07',
		showCancelButton: true,
		text: t('accessory:RESTORE_DIALOG'),
		title: t('accessory:RESTORE_TITLE'),
	});
	if (result.value) {
		await Accessory.callApiRestore(id);
		await delay(3000);
		await Swal.fire({
			confirmButtonColor: '#00cc07',
			title: t('accessory:RESTORE_CONFIRMATION'),
		});
	}

	return { ...obj };
}

export async function archive(obj: Type, id: string): Promise<Type> {
	const result: SweetAlertResult = await Swal.fire({
		cancelButtonText: t('_general:CANCEL'),
		confirmButtonColor: '#ff0004',
		confirmButtonText: t('_general:ARCHIVE'),
		icon: 'warning',
		iconColor: '#ff0004',
		showCancelButton: true,
		text: t('accessory:ARCHIVE_DIALOG'),
		title: t('accessory:ARCHIVE_TITLE'),
	});
	if (result.value) {
		await Accessory.callApiArchive(id);
		await delay(3000);
		await Swal.fire({
			confirmButtonColor: '#00cc07',
			title: t('accessory:ARCHIVE_CONFIRMATION'),
		});
	}

	return { ...obj };
}
